import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { IconSearch, NamedLink, ResponsiveImage } from '../../../components';
import { getEcolabelFilterOptions } from '../../../config/ecolabels';

import { IconArrow } from './Icons';
import VerifiedBadge from './VerifiedBadge';
import {
  HERO_ECOLABEL_KEYS,
  HERO_PRODUCT_COUNT,
  pickHeroEcolabels,
  pickHeroProducts,
} from './heroShowcase';
import css from './SectionHero.module.css';

// How many category pills to show under the hero copy. The row is a quick
// jumping-off point rather than a full index (that's SectionCategories further
// down the page), so it's capped to keep the hero compact.
const MAX_PILLS = 6;

/**
 * A search box straight into the marketplace, so a shopper who knows what they
 * want doesn't have to find the search in the header first.
 */
const HeroSearch = () => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [keywords, setKeywords] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    const trimmed = keywords.trim();
    const searchParams = trimmed ? { keywords: trimmed } : {};
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  const label = intl.formatMessage({ id: 'LandingPage.hero.searchPlaceholder' });
  return (
    <form className={css.search} onSubmit={handleSubmit} role="search">
      <IconSearch rootClassName={css.searchIcon} />
      <input
        className={css.searchInput}
        type="search"
        name="keywords"
        value={keywords}
        onChange={e => setKeywords(e.target.value)}
        placeholder={label}
        aria-label={label}
        autoComplete="off"
      />
      <button type="submit" className={css.searchButton}>
        <FormattedMessage id="LandingPage.hero.searchButton" />
      </button>
    </form>
  );
};

// An ecolabel badge that links into search filtered by that ecolabel (when the
// hosted ecolabel field offers it as a filter).
const EcolabelLink = props => {
  const { ecolabel, filterLinks, className, children } = props;
  const search = filterLinks[ecolabel.key];
  return search ? (
    <NamedLink name="SearchPage" to={{ search }} className={className} title={ecolabel.label}>
      {children}
    </NamedLink>
  ) : (
    <span className={className} title={ecolabel.label}>
      {children}
    </span>
  );
};

const ProductTile = props => {
  const { listing, className, sizes } = props;
  const title = listing.attributes?.title || '';
  return (
    <NamedLink
      className={classNames(css.tile, className)}
      name="ListingPage"
      params={{ id: listing.id.uuid, slug: createSlug(title) }}
    >
      <ResponsiveImage
        rootClassName={css.tileImage}
        image={listing.images[0]}
        alt={title}
        variants={['listing-card', 'listing-card-2x']}
        sizes={sizes}
      />
    </NamedLink>
  );
};

// Takes a product's place when there aren't enough products with photos.
const EcolabelTile = props => {
  const { ecolabel, filterLinks, className } = props;
  return (
    <EcolabelLink
      ecolabel={ecolabel}
      filterLinks={filterLinks}
      className={classNames(css.tile, css.ecolabelTile, className)}
    >
      <img className={css.ecolabelTileBadge} src={ecolabel.src} alt="" />
      <span className={css.ecolabelTileLabel}>{ecolabel.label}</span>
    </EcolabelLink>
  );
};

// A magnifying glass with a tick in the lens: every product here has been
// independently checked.
const Magnifier = () => (
  <svg className={css.magnifier} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
    <path className={css.magnifierHandle} d="M41 41l15 15" />
    <circle className={css.magnifierLens} cx="26" cy="26" r="19" />
    <path className={css.magnifierTick} d="M17.5 26.5l6 6 11-11.5" />
  </svg>
);

const TILE_POSITIONS = [css.tileA, css.tileB, css.tileC, css.tileD];
const TILE_SIZES = '(max-width: 1023px) 45vw, 260px';
const FLOAT_POSITIONS = [css.floatA, css.floatB, css.floatC];

/**
 * The hero image: photos of the newest products, one per vendor, in a
 * staggered grid with three ecolabels and a magnifying glass over a soft
 * brand backdrop.
 */
const HeroShowcase = props => {
  const { heroListings, listings, loading, listingFields } = props;
  const intl = useIntl();

  // The hero's own pool first; the featured grid's listings cover a failed fetch.
  const products = loading ? [] : pickHeroProducts([...heroListings, ...listings]);
  const missingProducts = loading ? 0 : HERO_PRODUCT_COUNT - products.length;
  const ecolabels = pickHeroEcolabels(missingProducts);
  const floating = ecolabels.slice(0, HERO_ECOLABEL_KEYS.length);
  const tileEcolabels = ecolabels.slice(HERO_ECOLABEL_KEYS.length);

  const { fieldKey, options } = getEcolabelFilterOptions(listingFields);
  const filterLinks = options.reduce(
    (acc, { option, ecolabel }) => ({
      ...acc,
      [ecolabel.key]: `?pub_${fieldKey}=has_any:${encodeURIComponent(option)}`,
    }),
    {}
  );

  return (
    <div
      className={css.showcase}
      role="group"
      aria-label={intl.formatMessage({ id: 'LandingPage.hero.showcaseLabel' })}
    >
      <div className={css.backdrop} aria-hidden="true" />

      {TILE_POSITIONS.map((position, i) => {
        if (loading) {
          return <div key={i} className={classNames(css.tile, css.tileSkeleton, position)} />;
        }
        const listing = products[i];
        const ecolabel = listing ? null : tileEcolabels[i - products.length];
        return listing ? (
          <ProductTile
            key={listing.id.uuid}
            listing={listing}
            className={position}
            sizes={TILE_SIZES}
          />
        ) : ecolabel ? (
          <EcolabelTile
            key={ecolabel.key}
            ecolabel={ecolabel}
            filterLinks={filterLinks}
            className={position}
          />
        ) : null;
      })}

      {floating.map((ecolabel, i) => (
        <EcolabelLink
          key={ecolabel.key}
          ecolabel={ecolabel}
          filterLinks={filterLinks}
          className={classNames(css.floating, FLOAT_POSITIONS[i])}
        >
          <img className={css.floatingBadge} src={ecolabel.src} alt={ecolabel.label} />
        </EcolabelLink>
      ))}

      <Magnifier />
    </div>
  );
};

/**
 * Homepage hero.
 *
 * Headline, a search box, the main call to action and category quick links on
 * the left; product photos and ecolabels on the right (under the copy on
 * phones). The quick links are the Listing Categories configured in Sharetribe (Console → Listings → Categories) and link into search on the
 * structured category param, so categories added later appear here with no
 * code change.
 *
 * @param {Object} props
 * @param {Array<{id: string, name: string}>} props.categories config.categoryConfiguration.categories
 * @param {string} props.categoryKey config.categoryConfiguration.key
 * @param {Array<Object>} [props.listings] listings already loaded for the page
 * @param {boolean} [props.queryInProgress] whether they are still loading
 * @param {Array<Object>} [props.heroListings] the newest listings the hero picks from
 * @param {boolean} [props.heroQueryInProgress] whether they are still loading
 * @param {Array<Object>} [props.listingFields] config.listing.listingFields
 */
const SectionHero = props => {
  const {
    categories = [],
    categoryKey = 'categoryLevel',
    listings = [],
    queryInProgress,
    heroListings = [],
    heroQueryInProgress,
    listingFields,
  } = props;
  const pills = categories.slice(0, MAX_PILLS);

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.copy}>
          <span className={css.eyebrow}>
            <VerifiedBadge className={css.eyebrowBadge} />
            <FormattedMessage
              id="LandingPage.hero.eyebrow"
              defaultMessage="Sustainability claims checked against evidence"
            />
          </span>
          <h1 className={css.title}>
            <FormattedMessage
              id="LandingPage.hero.title"
              defaultMessage="Shop products with verified sustainability claims."
            />
          </h1>
          <p className={css.subtitle}>
            <FormattedMessage
              id="LandingPage.hero.subtitle"
              defaultMessage="Independent brands whose specific sustainability claims we've checked against evidence, so you can see exactly what each product's credentials are before you buy."
            />
          </p>

          <HeroSearch />

          <div className={css.ctaRow}>
            <NamedLink name="SearchPage" className={css.primaryCta}>
              <FormattedMessage
                id="LandingPage.hero.primaryCta"
                defaultMessage="Browse marketplace"
              />
              <IconArrow className={css.ctaArrow} />
            </NamedLink>
          </div>

          {pills.length > 0 ? (
            <ul className={css.pills}>
              {pills.map(({ id, name }) => (
                <li key={id} className={css.pillItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{ search: `?pub_${categoryKey}1=${encodeURIComponent(id)}` }}
                    className={css.pill}
                  >
                    {name}
                  </NamedLink>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <HeroShowcase
          heroListings={heroListings}
          listings={listings}
          loading={queryInProgress || heroQueryInProgress}
          listingFields={listingFields}
        />
      </div>
    </section>
  );
};

export default SectionHero;
